import { type SchemaStatus, generateRequiredRangeStatus, generateWarningRangeStatus, rangeInterpolates } from '@redviking/argonaut-util/src/attribute-settings/validate-attribute-settings';
import {
    type AttributeSettingForAttributeLinker,
} from '@redviking/argonaut-util/types/attribute-settings/attribute-settings-config.zod';

export const validateAttributeSettingHasBeenFufilled = (attributeSetting: AttributeSettingForAttributeLinker, attributeIds: string[]): SchemaStatus => {
    if (attributeSetting.config.type === 'range') {
        const schemaName = attributeSetting.attributeSchema.name;
        const amountLinked = attributeSetting.attributeSchema.attributes.filter(a => attributeIds.includes(a.id)).length;
        let isLowerBoundSatified = false;
        if (attributeSetting.config.min.value !== null && amountLinked >= attributeSetting.config.min.value) {
            isLowerBoundSatified = true;
        }
        let isUpperBoundSatified = false;
        if (attributeSetting.config.max.value !== null && amountLinked <= attributeSetting.config.max.value) {
            isUpperBoundSatified = true;
        }
        let ret: SchemaStatus = isLowerBoundSatified && isUpperBoundSatified
            ? {
                type: 'success',
                icon: 'mdi-check',
                message: 'Allow range of at least {min} and at most {max} attributes from the {schemaName} schema to be linked',
                messagePlaintext: `Allow range of at least ${attributeSetting.config.min.value} and at most ${attributeSetting.config.max.value} attributes from the ${schemaName} schema to be linked`,
                interpolates: rangeInterpolates({
                    schemaName,
                    maxValue: attributeSetting.config.max.value,
                    minValue: attributeSetting.config.min.value,
                }),
            }
            : generateWarningRangeStatus({
                schemaName,
                maxValue: attributeSetting.config.max.value,
                minValue: attributeSetting.config.min.value,
            });
        if (ret.type === 'warning' && attributeSetting.config.strict) {
            ret = generateRequiredRangeStatus({
                schemaName,
                maxValue: attributeSetting.config.max.value,
                minValue: attributeSetting.config.min.value,
            });
        }
        return ret;
    } else if (attributeSetting.config.type === 'none') {
        const schemaName = attributeSetting.attributeSchema.name;
        const amountLinked = attributeSetting.attributeSchema.attributes.filter(a => attributeIds.includes(a.id)).length;
        let ret: SchemaStatus = amountLinked === 0
            ? {
                type: 'success',
                icon: 'mdi-check',
                message: '{doNotAllow} any attribute from the {schemaName} schema to be linked',
                messagePlaintext: `Do not allow any attribute from the ${schemaName} schema to be linked`,
                interpolates: [
                    {
                        type: 'code',
                        name: 'doNotAllow',
                        value: 'Do not allow',
                    },
                    {
                        type: 'code',
                        value: schemaName,
                        name: 'schemaName',
                    },
                ],
            }
            : {
                type: 'warning',
                icon: 'mdi-alert-outline',
                message: 'Setting {suggests} {no} attributes from the {schemaName} schema should be linked',
                messagePlaintext: `Setting suggests no attributes from the ${schemaName} schema to be linked`,
                interpolates: [
                    {
                        type: 'b',
                        name: 'suggests',
                        value: 'suggests',
                    },
                    {
                        name: 'no',
                        value: 'no',
                        type: 'code',
                    },
                    {
                        type: 'code',
                        value: schemaName,
                        name: 'schemaName',
                    },
                ],
            };
        if (ret.type === 'warning' && attributeSetting.config.strict) {
            ret = {
                type: 'error',
                icon: 'mdi-alert-octagon-outline',
                message: 'Setting {requires} {no} attributes from the {schemaName} schema should be linked',
                messagePlaintext: `Setting requires no attributes from the ${schemaName} schema to be linked`,
                interpolates: [
                    {
                        type: 'b',
                        name: 'requires',
                        value: 'requires',
                    },
                    {
                        name: 'no',
                        value: 'no',
                        type: 'code',
                    },
                    {
                        type: 'code',
                        value: schemaName,
                        name: 'schemaName',
                    },
                ],
            };
        }
        return ret;
    } else {
        const unknownConfig: never = attributeSetting.config;
        return {
            message: '',
            type: 'error',
            interpolates: [],
            icon: 'mdi-alert-octagon-outline',
            messagePlaintext: `Unknown attribtue setting type: ${JSON.stringify(unknownConfig)}`,
        };
    }
};
